import {fetchAuthSession} from 'aws-amplify/auth';

import {ROUTE_URLS} from '../../constants/routes';
import {basename} from '../history';

const FSSO_RETRY_ATTEMPTS_KEY = 'FSSO_RETRY_ATTEMPTS_KEY';
const FSSO_RETRY_ATTEMPTS_CODE = 'RETRYING';

let csrfToken = null;

function getFssoRetryBlock() {
  const FSSORetries = globalThis.sessionStorage.getItem(FSSO_RETRY_ATTEMPTS_KEY);
  return FSSORetries === FSSO_RETRY_ATTEMPTS_CODE;
}

function setFssoRetryBlock(setBlocked) {
  if (setBlocked) {
    globalThis.sessionStorage.setItem(FSSO_RETRY_ATTEMPTS_KEY, FSSO_RETRY_ATTEMPTS_CODE);
  } else {
    globalThis.sessionStorage.removeItem(FSSO_RETRY_ATTEMPTS_KEY);
  }
}

const MAX_TOKEN_RETRIES = 2;
const TOKEN_RETRY_DELAY_MS = 1000;

/**
 * Get Auth currentSession access token's JWT token. if fails then triggers
 * Auth.federatedSignIn with customState value of current location in coded format
 *
 * Retries on transient failures before redirecting to login.
 *
 * @returns {Promise<string>}
 */
function getToken(params, _retryCount = 0) {
  return fetchAuthSession(params)
    .then(({tokens}) => {
      const {accessToken} = tokens ?? {};
      return accessToken;
    })
    .catch((error) => {
      // Session explicitly expired or user not authenticated — redirect immediately
      const isSessionExpired =
        error?.name === 'NotAuthorizedException' ||
        error?.message?.includes('No current user') ||
        error?.name === 'UserUnAuthenticatedException';

      if (isSessionExpired) {
        if (!getFssoRetryBlock()) {
          const encodedUrl = encodeUrl(globalThis.location.href);
          globalThis.location.assign(`${globalThis.location.origin}${ROUTE_URLS.LOGIN}?redirectUrl=${encodedUrl}`);
        }
        return undefined;
      }

      // Transient failure (network error, temporary Cognito issue) — retry
      if (_retryCount < MAX_TOKEN_RETRIES) {
        return new Promise((resolve) => setTimeout(resolve, TOKEN_RETRY_DELAY_MS)).then(() =>
          getToken(params, _retryCount + 1),
        );
      }

      // Exhausted retries — redirect to login as last resort
      if (!getFssoRetryBlock()) {
        const encodedUrl = encodeUrl(globalThis.location.href);
        globalThis.location.assign(`${globalThis.location.origin}${ROUTE_URLS.LOGIN}?redirectUrl=${encodedUrl}`);
      }
    });
}

/**
 * Encodes url value using bota() and string placing of equal `=` and forward-slash `/` to
 * work with amplify's encoding/decoding process
 *
 * @param url
 * @returns {string}
 */
function encodeUrl(url) {
  return btoa(url).replace(/=/g, '_').replace(/\//g, '-');
}

/**
 * Decodes encodedUrl value using string placing of equal `=` and forward-slash `/` and aotb() to
 * work with amplify's encoding/decoding process
 *
 * @param codedUrl
 * @returns {string}
 */
function decodeUrl(codedUrl) {
  const unescapedString = codedUrl.replace(/_/g, '=').replace(/-/g, '/');

  return atob(unescapedString);
}

/**
 * Decodes encodedUrl and compares current window.location.href to url value, if different does a
 * windows.location.assign(url)
 *
 * @param encodedUrl
 */
function redirectToEncodedUrl(encodedUrl) {
  const decodedUrl = decodeUrl(encodedUrl);
  try {
    const url = new URL(decodedUrl);
    const locationChanged = globalThis.location.href !== url.href;

    if (locationChanged) {
      if (url.origin === globalThis.location.origin) {
        globalThis.location.assign(url);
      } else {
        console.error('redirect url does not match origin', decodedUrl);
        globalThis.location.assign('/');
      }
    }
  } catch (e) {
    // If the decoded url is not a valid URL string, the URL constructor throws a TypeError.
    console.error('Invalid URL:', decodedUrl);
  }
}

/**
 * setter saving of csrf token
 * @param token
 */
function setCsrfToken(token) {
  csrfToken = token;
}

/**
 * getter for csrf token
 * @returns {string|string}
 */
function getCsrfToken() {
  return csrfToken;
}

export default {
  basename,
  getToken,
  encodeUrl,
  decodeUrl,
  redirectToEncodedUrl,
  getFssoRetryBlock,
  setFssoRetryBlock,
  getCsrfToken,
  setCsrfToken,
};
